import type { SegmentedControlItem } from '@byecode/ui'
import { Flex, IconFont, Popover, SegmentedControl, Tooltip } from '@byecode/ui'
import type { BackgroundNoImageProtocol } from '@lighthouse/core'
import { BACKGROUND_TYPE, TEXT_ALIGNMENT } from '@lighthouse/core'
import {
    FilledInput,
    FillPickerPopoverDropdown,
    Group4ByecodeUi,
    ListItem4ByecodeUi,
    PopoverDropdownWithClose,
    TEXT_FONT_NORMAL
} from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { FontFamilyControl } from '@/components/BlockSettings/Common/FontFamilyControl'
import { InheritLabel } from '@/components/InheritLabel'
import { LayoutSizeInput } from '@/components/StyleSetting/AutoLayoutSetting/LayoutSizeInput'
import { FillColor } from '@/components/StyleSetting/components/FillColor'
import { useSetExtendsKeys } from '@/components/StyleSetting/hooks/useMouseEventDistance'
import { useGetColorVariableOptions } from '@/hooks/useGetColorVariableOptions'

const TEXT_ALIGN_OPTIONS: SegmentedControlItem[] = [
    {
        label: (
            <Tooltip title="居左">
                <IconFont type="TextAlignLeft" size={16} />
            </Tooltip>
        ),
        value: TEXT_ALIGNMENT.left
    },
    {
        label: (
            <Tooltip title="居中">
                <IconFont type="TextAlignCenter" size={16} />
            </Tooltip>
        ),
        value: TEXT_ALIGNMENT.center
    },
    {
        label: (
            <Tooltip title="居右">
                <IconFont type="TextAlignRight" size={16} />
            </Tooltip>
        ),
        value: TEXT_ALIGNMENT.right
    }
]

const DEFAULT_FONT_SIZE = { size: 14, unit: 'px' }
const DEFAULT_ROW_SIZE = { size: 40, unit: 'px' }
const DEFAULT_COL_SIZE = { size: 100, unit: 'px' }

const MultiDimensionalSettingItem: React.FC<{ customViewDsId: string | undefined; isRow?: boolean }> = ({ customViewDsId, isRow }) => {
    const { control } = useFormContext()
    const { handleSetBreakKey } = useSetExtendsKeys()

    const variableTypeOption = useGetColorVariableOptions({
        customViewDsId
    })

    return (
        <>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="字号" name={`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.fontSize`} />
                <Controller
                    name={`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.fontSize`}
                    control={control}
                    defaultValue={DEFAULT_FONT_SIZE}
                    render={({ field }) => (
                        <LayoutSizeInput
                            iconType="TextAa"
                            disableUnitChange
                            value={field.value}
                            onChange={v => {
                                field.onChange(v)
                                handleSetBreakKey(`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.fontSize`)
                            }}
                        />
                    )}
                />
            </ListItem4ByecodeUi>

            <Controller
                name={`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.fontFamily`}
                control={control}
                defaultValue={TEXT_FONT_NORMAL}
                render={({ field }) => (
                    <FontFamilyControl
                        {...field}
                        onChange={v => {
                            field.onChange(v)
                            handleSetBreakKey(`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.fontFamily`)
                        }}
                    />
                )}
            />

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="文本颜色" name={`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.color`} />
                <Controller
                    control={control}
                    name={`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.color`}
                    render={({ field }) => {
                        return (
                            <FillColor
                                label="文本颜色"
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey(`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.color`)
                                }}
                            />
                        )
                    }}
                />
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="对齐" name={`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.textAlign`} />
                <Controller
                    control={control}
                    name={`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.textAlign`}
                    defaultValue={TEXT_ALIGNMENT.left}
                    render={({ field }) => (
                        <SegmentedControl
                            value={field.value}
                            onChange={val => {
                                field.onChange(val)
                                handleSetBreakKey(`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.textAlign`)
                            }}
                            data={TEXT_ALIGN_OPTIONS}
                            fullWidth
                            style={{ width: 180 }}
                        />
                    )}
                />
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="背景色" name={`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.background`} />
                <Controller
                    name={`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.background`}
                    control={control}
                    render={({ field }) => (
                        <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={150}>
                            <Popover.Target>
                                <FilledInput
                                    style={{ width: 180 }}
                                    value={field.value}
                                    onClear={() => {
                                        field.onChange({ type: BACKGROUND_TYPE.color })

                                        handleSetBreakKey(`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.background`)
                                    }}
                                    variableTypeOption={variableTypeOption}
                                />
                            </Popover.Target>
                            <FillPickerPopoverDropdown
                                title="填充"
                                value={field.value}
                                onChange={val => {
                                    if (isDeepEqual(val, field.value)) {
                                        return
                                    }
                                    field.onChange(val)
                                    handleSetBreakKey(`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.background`)
                                }}
                                enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient, BACKGROUND_TYPE.variable]}
                                variableTypeOption={variableTypeOption}
                            />
                        </Popover>
                    )}
                />
            </ListItem4ByecodeUi>

            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel
                    label={isRow ? '表头行高' : '表头列宽'}
                    name={`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.size`}
                />
                <Controller
                    name={`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.size`}
                    control={control}
                    defaultValue={isRow ? DEFAULT_ROW_SIZE : DEFAULT_COL_SIZE}
                    render={({ field }) => (
                        <LayoutSizeInput
                            iconType={isRow ? 'Height' : 'Width'}
                            disableUnitChange
                            value={field.value}
                            onChange={v => {
                                field.onChange(v)
                                handleSetBreakKey(`breakPoint.multiDimensionalStyle.${isRow ? 'row' : 'col'}.size`)
                            }}
                        />
                    )}
                />
            </ListItem4ByecodeUi>
        </>
    )
}

export const MultiDimensionalSetting: React.FC<{ customViewDsId: string | undefined }> = ({ customViewDsId }) => {
    return (
        <Group4ByecodeUi label="视图样式">
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="表头行" name="breakPoint.multiDimensionalStyle.row" />

                <Popover width={342} withinPortal position="left-start" autoUpdate={false} offsetOptions={72}>
                    <Popover.Target>
                        <Flex styles={{ root: { cursor: 'pointer', flex: 1, alignSelf: 'stretch' } }} justifyContent="flex-end">
                            <IconFont type="ArrowRightSmall" />
                        </Flex>
                    </Popover.Target>
                    <PopoverDropdownWithClose title="表头行">
                        <MultiDimensionalSettingItem isRow customViewDsId={customViewDsId} />
                    </PopoverDropdownWithClose>
                </Popover>
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label="表头列" name="breakPoint.multiDimensionalConfig.col" />

                <Popover width={342} withinPortal position="left-start" autoUpdate={false} offsetOptions={72}>
                    <Popover.Target>
                        <Flex styles={{ root: { cursor: 'pointer', flex: 1, alignSelf: 'stretch' } }} justifyContent="flex-end">
                            <IconFont type="ArrowRightSmall" />
                        </Flex>
                    </Popover.Target>
                    <PopoverDropdownWithClose title="表头列">
                        <MultiDimensionalSettingItem customViewDsId={customViewDsId} />
                    </PopoverDropdownWithClose>
                </Popover>
            </ListItem4ByecodeUi>
        </Group4ByecodeUi>
    )
}
